import { useEffect, useState } from 'react';
import {
  Box, Button, HStack, Stack, Text, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { fetchMyReferral, ReferralMe } from './referralApi';

/**
 * "Invite friends" card — rendered on the Credits page. Fetches the
 * user's code lazily on mount. Silently returns nothing when referrals
 * are disabled server-side (fetchMyReferral returns null on 403), so
 * enabling/disabling the feature for a whole product is a single
 * Settings.<app>.referrals.enabled flip.
 *
 * Share surface: native Web Share API when available (mobile browsers +
 * installed PWAs), copy-to-clipboard fallback otherwise. The shared link
 * carries the code as `?ref=CODE` — landing on any page with that param
 * writes it to localStorage via referralStore.captureFromUrl(), so the
 * new user's eventual signup finds it regardless of which page they
 * first hit.
 */
export function InviteCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [ref, setRef] = useState<ReferralMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchMyReferral();
      if (!cancelled) {
        setRef(data);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;      // don't flash a placeholder — fast enough
  if (!ref) return null;         // referrals disabled server-side, hide entirely

  const shareUrl = `${window.location.origin}/?ref=${encodeURIComponent(ref.code)}`;
  const referrerReward = ref.config.signupRewardReferrer;
  const refereeReward = ref.config.signupRewardReferee;
  const purchaseReward = ref.config.firstPurchaseRewardReferrer;

  const doShare = async () => {
    const shareData = {
      title: t('invite.shareTitle', 'Make songs with MixGenie'),
      text: refereeReward > 0
        ? t('invite.shareText', 'Join me on MixGenie — we both get {{n}} free song credits.', { n: refereeReward })
        : t('invite.shareTextNoBonus', 'Join me on MixGenie — free song generator.'),
      url: shareUrl,
    };
    // Native share first (mobile + installed PWA). Falls back cleanly on
    // desktop browsers that don't implement it.
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch { /* user cancelled — nothing to do */ }
    }
    // Copy fallback.
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      toast({ title: t('invite.copied', 'Invite link copied'), status: 'success', duration: 1800 });
    } catch {
      toast({ title: t('invite.copyFailed', "Couldn't copy — long-press the link to share manually"), status: 'warning' });
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(ref.code);
      toast({ title: t('invite.codeCopied', 'Code copied'), status: 'success', duration: 1500 });
    } catch { /* noop */ }
  };

  return (
    <Box
      mt={6}
      p={{ base: 4, md: 5 }}
      borderRadius="18px"
      border="1px solid"
      borderColor="whiteAlpha.150"
      bg="rgba(20, 14, 36, 0.6)"
    >
      <Stack spacing={3}>
        <HStack justify="space-between" align="flex-start" gap={3}>
          <Stack spacing={1} flex="1" minW={0}>
            <Text fontSize="lg" fontWeight={700} color="whiteAlpha.900">
              🎁 {t('invite.title', 'Invite friends, earn credits')}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.700" style={{ textWrap: 'balance' as any }}>
              {refereeReward > 0
                ? t('invite.subtitle', 'You both get {{r}} credits when they sign up{{p}}.', {
                    r: refereeReward,
                    p: purchaseReward > 0 ? t('invite.subtitlePurchase', ' — plus {{n}} more when they buy their first pack', { n: purchaseReward }) : '',
                  })
                : t('invite.subtitleReferrerOnly', 'Get {{n}} credits every time a friend signs up with your link.', { n: referrerReward })}
            </Text>
          </Stack>
        </HStack>

        {/* Code chip — copy-to-clipboard, mono for readability */}
        <HStack
          spacing={2}
          p={2}
          borderRadius="12px"
          bg="whiteAlpha.100"
          border="1px solid"
          borderColor="whiteAlpha.100"
          justify="space-between"
        >
          <Text
            fontFamily="ui-monospace, SF Mono, Menlo, monospace"
            fontSize="lg"
            fontWeight={700}
            color="whiteAlpha.900"
            letterSpacing="0.1em"
            px={2}
          >
            {ref.code}
          </Text>
          <Button size="sm" variant="ghost" color="whiteAlpha.700" onClick={copyCode}>
            {t('invite.copyCode', 'Copy code')}
          </Button>
        </HStack>

        <Button
          size="lg"
          w="full"
          borderRadius="12px"
          bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
          color="white"
          fontWeight={700}
          onClick={doShare}
          _hover={{ bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)', transform: 'translateY(-1px)' }}
          _active={{ transform: 'translateY(0)' }}
        >
          {copied ? t('invite.linkCopied', 'Link copied ✓') : t('invite.share', 'Share your invite link')}
        </Button>

        {/* Stats — only rendered when the user has something to show. New
            users see a clean CTA, active referrers see a "here's what
            you've earned" nudge. */}
        {ref.stats.qualified + ref.stats.granted > 0 && (
          <HStack justify="space-between" fontSize="sm" color="whiteAlpha.700" pt={1}>
            <Text>
              {t('invite.statsInvites', '{{n}} joined', { n: ref.stats.qualified + ref.stats.granted })}
            </Text>
            <Text fontFamily="ui-monospace, SF Mono, Menlo, monospace">
              ⚡ {ref.stats.totalCreditsEarned} {t('invite.statsEarned', 'earned')}
            </Text>
          </HStack>
        )}
      </Stack>
    </Box>
  );
}
